import { BaseResponseSchema, getRequiredMessage } from '@monetize/types/common';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import * as z from 'zod';
import { CustomFieldRecordSchema } from './customFields.types';
import { PriceModelEnum, ProductTypeEnumZ } from './enums.types';
import {
  ProductStatusEnumZ,
  ProrationPolicyEnum,
  RecognitionMethodEnumZ,
} from './product.types';
import { UsageTypeResSchema, UsageTypeStatusEnumZ } from './usage.types';

export enum PriceStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}
export const PriceStatusEnumz = z.enum(PriceStatusEnum);

export const PRICE_MODEL_ENUM_DISPLAY: {
  [key in PriceModelEnum]: string;
} = {
  VOLUME: 'Volume',
  TIERED: 'Tiered',
  FLAT: 'Flat',
  CUSTOM: 'Custom',
  PERCENT_OF_TOTAL: 'Percent Of Total',
  BLOCK: 'Block',
};

export const PriceModelEnumZ = z.enum(PriceModelEnum, {
  error: () => {
    return 'Select a pricing model.';
  },
});

function refineTierBounds(
  val: number | string | null,
  valueIfEmptyOrNull: boolean,
) {
  if (isNil(val)) {
    return valueIfEmptyOrNull;
  }
  return isNumber(val)
    ? val >= 0
    : !isNaN(parseInt(val, 10)) && parseInt(val, 10) >= 0;
}

// used in POST /api/offerings/{offeringId}/rates
export const PriceReqSchema = z.object({
  productId: z.string().nullish(),
  from: z
    .union([z.string(), z.number()])
    .refine((val) => refineTierBounds(val, false), {
      error: 'From must be a valid number',
    })
    .transform((val) => Number(val)),
  to: z
    .number()
    .nullable()
    .transform((val) => {
      if (isNumber(val)) {
        return val;
      }
      return null;
    })
    .refine((val) => refineTierBounds(val, true), {
      error: 'To must be a valid number',
    })
    .optional(),

  amount: z
    .union([z.string(), z.number()])
    .nullish()
    .refine((val) => val !== null && val !== undefined && val !== '', {
      message: getRequiredMessage('Amount'),
    })
    .transform((val) => Number(val)),
  priceModel: PriceModelEnumZ,
  description: z.string().nullish(),
  blockSize: z
    .number()
    .min(1, { message: 'Block Size cannot be empty' })
    .nullable()
    .optional()
    .transform((val) => {
      if (isNumber(val)) {
        return val;
      }
      return null;
    }),
});
export type IPriceReqSchema = z.infer<typeof PriceReqSchema>;

export const PricesReqSchema = z
  .object({})
  .catchall(z.object({ prices: PriceReqSchema.array() }));
export type IPricesReqSchema = z.infer<typeof PricesReqSchema>;

// returned as part of rate from GET /api/rates/{$rateId}
export const PriceResSchema = BaseResponseSchema.extend({
  from: z.number(),
  to: z.number().nullable(),
  amount: z.number(),
  description: z.string().nullish(),
  priceModel: PriceModelEnumZ,
  product: z.object({
    id: z.string(),
    customId: z.string().nullish(),
    financeId: z.string().nullish(),
    name: z.string(),
    description: z.string().nullish(),
    status: ProductStatusEnumZ,
    productType: ProductTypeEnumZ,
    usageType: z
      .object({
        id: z.string(),
        name: z.string(),
        pluralName: z.string(),
        description: z.string().nullish(),
        unitName: z.string(),
        decimalPlaces: z.string(),
        status: UsageTypeStatusEnumZ,
      })
      .array()
      .nullable(),
  }),
  blockSize: z.number().nullable(),
});
export type IPriceResSchema = z.infer<typeof PriceResSchema>;

export const PriceUnderProductSchemaUI = z.object({
  customId: z.string(),
  description: z.string(),
  financeId: z.string(),
  id: z.string(),
  name: z.string(),
  prices: z.array(PriceResSchema),
  productType: ProductTypeEnumZ,
  status: ProductStatusEnumZ,
  usageType: UsageTypeResSchema,
  recognitionMethod: RecognitionMethodEnumZ,
  locked: z.boolean(),
  customFields: CustomFieldRecordSchema,
  taxItemCode: z.string().nullish(),
  taxExempt: z.boolean().catch(false),
  prorationPolicy: z.enum(ProrationPolicyEnum).nullable(),
});

export type IPriceUnderProductSchemaUI = z.infer<
  typeof PriceUnderProductSchemaUI
>;

export type IPriceTierSettingsReq = {
  priceDisplayMode: string;
  displayedPriceIds: Record<string, string[]>;
};

export enum PriceTiersEnum {
  ALL_PRICES = 'ALL_PRICES',
  USED_PRICES = 'USED_PRICES',
  CUSTOM_PRICES = 'CUSTOM_PRICES',
}
